import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM, Text, Icon, Spinner, Button } from "components/atoms";
import { DateRangePicker } from "components/molecules/DateRangePicker";
import { getDateStringFromToday } from "utils/TimeUtil";
import { evaluationGroupApi } from "services/api/aiReviewTest/evaluationGroupApi";
import { AIReviewEvaluationGroup } from "common/models/types";
import { ToastManager, SlideManager } from "components/organisms/CommonUI";
import EvaluationGroupDetail from "./EvaluationGroupDetail";

const Container = styled.div`
  width: 100%;
  max-width: 100%;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled(Text)`
  font-size: 24px;
  font-weight: 600;
  color: #333;
`;

const FilterSection = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  width: 100%;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;


const FilterLabel = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  min-width: 100px;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  width: 300px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const TableContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }
`;

const Th = styled.th<{ sortable?: boolean }>`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  white-space: nowrap;
  cursor: ${({ sortable }) => (sortable ? "pointer" : "default")};
  user-select: none;

  &:hover {
    ${({ sortable }) => sortable && "background: #f3f4f6;"}
  }
`;

const SortableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #374151;
`;

const GroupKeyCell = styled(Td)`
  max-width: 300px;
  word-break: break-all;
  white-space: pre-wrap;
  line-height: 1.4;
  font-size: 12px;
`;

const ScoreCell = styled(Td)<{ isGood?: boolean }>`
  font-weight: 600;
  color: ${({ isGood }) => (isGood ? COLOR_SYSTEM.get("Green")?.[600] : COLOR_SYSTEM.get("Gray")?.[700])};
`;

const EmptyRow = styled.tr`
  td {
    padding: 48px;
    text-align: center;
    color: #9ca3af;
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

type SortField = "createdAt" | "averageScore" | "minScore" | "maxScore" | "stdDeviationScore" | "avgCost" | "totalExecutions";
type SortOrder = "asc" | "desc";

const AdminAIReviewTestAnalysisPage: React.FC = () => {
  const [allEvaluationGroups, setAllEvaluationGroups] = useState<AIReviewEvaluationGroup[]>([]);
  const [displayedGroups, setDisplayedGroups] = useState<AIReviewEvaluationGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState({
    from: getDateStringFromToday({ days: -30 }),
    to: getDateStringFromToday(),
  });
  const [searchKeyword, setSearchKeyword] = useState("");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  // 클라이언트 사이드 정렬 및 필터링 함수
  const sortAndFilterGroups = (
    groups: AIReviewEvaluationGroup[],
    field: SortField,
    order: SortOrder,
    keyword: string
  ) => {
    let filtered = [...groups];
    
    // 키워드 필터링
    if (keyword) {
      filtered = filtered.filter(group => 
        group.testGroupKey.toLowerCase().includes(keyword.toLowerCase())
      );
    }
    
    // 정렬
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (field) {
        case "averageScore":
          aValue = a.averageScore || 0;
          bValue = b.averageScore || 0;
          break;
        case "minScore":
          aValue = a.minScore || 0;
          bValue = b.minScore || 0;
          break;
        case "maxScore":
          aValue = a.maxScore || 0;
          bValue = b.maxScore || 0;
          break;
        case "stdDeviationScore":
          aValue = a.stdDeviationScore || 0;
          bValue = b.stdDeviationScore || 0;
          break;
        case "avgCost":
          aValue = a.avgCost || 0;
          bValue = b.avgCost || 0;
          break;
        case "totalExecutions":
          aValue = a.totalExecutions || 0;
          bValue = b.totalExecutions || 0;
          break;
        case "createdAt":
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          aValue = 0;
          bValue = 0;
      }
      
      if (order === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    setDisplayedGroups(filtered);
  };

  const fetchEvaluationGroups = async () => {
    try {
      setIsLoading(true);
      // endDate에 23:59:59 추가
      const endDateWithTime = selectedDateRange.to ? `${selectedDateRange.to} 23:59:59` : undefined;
      
      const response = await evaluationGroupApi.findAll({
        startDate: selectedDateRange.from,
        endDate: endDateWithTime,
        search: searchKeyword || undefined,
        hidden: false,
      });

      if (response.data.error === 0 && response.data.data) {
        const groups = response.data.data || [];
        setAllEvaluationGroups(groups);
        sortAndFilterGroups(groups, sortField, sortOrder, searchKeyword);
      } else {
        setAllEvaluationGroups([]);
        setDisplayedGroups([]);
      }
    } catch (error) {
      console.error("Failed to fetch evaluation groups:", error);
      ToastManager.show({
        content: "평가 그룹 목록을 불러오는데 실패했습니다.",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 초기 로드 시에는 데이터를 가져오지 않음
  useEffect(() => {
    setIsLoading(false);
  }, []);

  // 정렬, 필터링이 변경될 때는 기존 데이터에서 정렬/필터링만 수행
  useEffect(() => {
    if (allEvaluationGroups.length > 0) {
      sortAndFilterGroups(allEvaluationGroups, sortField, sortOrder, searchKeyword);
    }
  }, [sortField, sortOrder, searchKeyword, allEvaluationGroups]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const handleGroupClick = (group: AIReviewEvaluationGroup) => {
    SlideManager.show({
      body: (
        <EvaluationGroupDetail
          group={group}
          onClose={() => SlideManager.hide()}
          onUpdate={(updatedGroup) => {
            // 업데이트된 그룹 데이터를 리스트에 반영
            const updatedGroups = allEvaluationGroups.map((g) =>
              g.id === updatedGroup.id ? updatedGroup : g
            );
            setAllEvaluationGroups(updatedGroups);
            sortAndFilterGroups(updatedGroups, sortField, sortOrder, searchKeyword);
          }}
        />
      ),
      width: "1200px",
      direction: "right",
      needCloseButton: true,
    });
  };

  const formatScore = (score: number | null | undefined) => {
    if (score === null || score === undefined) return "-";
    return Math.round(score).toString();
  };

  const formatCost = (cost: number | null | undefined) => {
    if (cost === null || cost === undefined) return "-";
    const numCost = typeof cost === 'number' ? cost : parseFloat(cost as any);
    if (isNaN(numCost)) return "-";
    return `$${numCost.toFixed(4)}`;
  };

  const formatDateTime = (dateString: any) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };


  return (
    <Container>
      <Header>
        <Title>AI Review 평가 분석</Title>
      </Header>

      <FilterSection>
        <FilterRow>
          <FilterLabel>조회 기간</FilterLabel>
          <DateRangePicker
            startDate={selectedDateRange.from}
            endDate={selectedDateRange.to}
            disabled={false}
            onChangeDate={(type, date) => {
              if (type === "start") {
                setSelectedDateRange({ ...selectedDateRange, from: date.toISOString().split("T")[0] });
              } else {
                setSelectedDateRange({ ...selectedDateRange, to: date.toISOString().split("T")[0] });
              }
            }}
          />
          <Button
            size="Medium"
            type="IconWithText"
            variant="Contained"
            color="Primary"
            icon="Refresh"
            left
            onClick={fetchEvaluationGroups}
            disabled={isLoading}
          >
            {isLoading ? "불러오는 중..." : "불러오기"}
          </Button>
        </FilterRow>
        <FilterRow>
          <FilterLabel>테스트 그룹</FilterLabel>
          <SearchInput
            type="text"
            placeholder="테스트 그룹 키로 검색"
            value={searchKeyword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchKeyword(e.target.value);
            }}
          />
        </FilterRow>
      </FilterSection>

      {isLoading ? (
        <LoadingContainer>
          <Spinner size="Large" />
        </LoadingContainer>
      ) : allEvaluationGroups.length === 0 && displayedGroups.length === 0 ? (
        <EmptyContainer>
          <Text style={{ fontSize: '16px', color: COLOR_SYSTEM.get("Gray")?.[500] }}>
            평가 그룹을 조회하려면 "불러오기" 버튼을 클릭하세요.
          </Text>
        </EmptyContainer>
      ) : (
        <>
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>테스트 그룹</Th>
                  <Th>평가 모델</Th>
                  <Th sortable onClick={() => handleSort("averageScore")}>
                    <SortableHeader>
                      평균 점수
                      <Icon
                        name={sortField === "averageScore" && sortOrder === "asc" ? "Chevron Up" : "Chevron Down"}
                        size={16}
                        color={sortField === "averageScore" ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[400]}
                      />
                    </SortableHeader>
                  </Th>
                  <Th sortable onClick={() => handleSort("minScore")}>
                    <SortableHeader>
                      최소 점수
                      <Icon
                        name={sortField === "minScore" && sortOrder === "asc" ? "Chevron Up" : "Chevron Down"}
                        size={16}
                        color={sortField === "minScore" ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[400]}
                      />
                    </SortableHeader>
                  </Th>
                  <Th sortable onClick={() => handleSort("maxScore")}>
                    <SortableHeader>
                      최대 점수
                      <Icon
                        name={sortField === "maxScore" && sortOrder === "asc" ? "Chevron Up" : "Chevron Down"}
                        size={16}
                        color={sortField === "maxScore" ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[400]}
                      />
                    </SortableHeader>
                  </Th>
                  <Th sortable onClick={() => handleSort("stdDeviationScore")}>
                    <SortableHeader>
                      표준편차
                      <Icon
                        name={sortField === "stdDeviationScore" && sortOrder === "asc" ? "Chevron Up" : "Chevron Down"}
                        size={16}
                        color={sortField === "stdDeviationScore" ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[400]}
                      />
                    </SortableHeader>
                  </Th>
                  <Th sortable onClick={() => handleSort("avgCost")}>
                    <SortableHeader>
                      평균 비용
                      <Icon
                        name={sortField === "avgCost" && sortOrder === "asc" ? "Chevron Up" : "Chevron Down"}
                        size={16}
                        color={sortField === "avgCost" ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[400]}
                      />
                    </SortableHeader>
                  </Th>
                  <Th sortable onClick={() => handleSort("totalExecutions")}>
                    <SortableHeader>
                      실행 횟수
                      <Icon
                        name={sortField === "totalExecutions" && sortOrder === "asc" ? "Chevron Up" : "Chevron Down"}
                        size={16}
                        color={sortField === "totalExecutions" ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[400]}
                      />
                    </SortableHeader>
                  </Th>
                  <Th>성공률</Th>
                  <Th sortable onClick={() => handleSort("createdAt")}>
                    <SortableHeader>
                      생성일시
                      <Icon
                        name={sortField === "createdAt" && sortOrder === "asc" ? "Chevron Up" : "Chevron Down"}
                        size={16}
                        color={sortField === "createdAt" ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[400]}
                      />
                    </SortableHeader>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {!displayedGroups || displayedGroups.length === 0 ? (
                  <EmptyRow>
                    <td colSpan={10}>평가 그룹이 없습니다.</td>
                  </EmptyRow>
                ) : (
                  displayedGroups.map((group: AIReviewEvaluationGroup) => (
                    <Tr key={group.id} onClick={() => handleGroupClick(group)}>
                      <GroupKeyCell>{group.testGroupKey.split('/').join('\n')}</GroupKeyCell>
                      <Td>
                        {group.evaluationModel || "기본 모델"}
                        {group.evaluationPromptId && ` (ID: ${group.evaluationPromptId})`}
                      </Td>
                      <ScoreCell isGood={(group.averageScore || 0) >= 80}>
                        {formatScore(group.averageScore)}
                      </ScoreCell>
                      <Td>{formatScore(group.minScore)}</Td>
                      <Td>{formatScore(group.maxScore)}</Td>
                      <Td>{formatScore(group.stdDeviationScore)}</Td>
                      <Td>{formatCost(group.avgCost)}</Td>
                      <Td>{group.totalExecutions}</Td>
                      <Td>
                        {group.totalExecutions > 0
                          ? `${Math.round((group.successCount / group.totalExecutions) * 100)}%`
                          : "-"}
                      </Td>
                      <Td>{formatDateTime(group.createdAt)}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </>
      )}
    </Container>
  );
};

export default AdminAIReviewTestAnalysisPage;